import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { lineReveal, fadeUp, EASE } from "@/lib/anim";

const LINES = [
  ["Institutional", "Infrastructure"],
  ["for", { text: "Real World Asset", blue: true }],
  ["Tokenization"],
];

export default function Hero({ onExplore, onInvest }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden pt-28 pb-20"
      data-testid="hero"
    >
      <motion.div
        style={{ y: glowY }}
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] blue-glow pointer-events-none"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.15]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(circle at center, black 0%, transparent 70%)",
        }}
      />

      <motion.div style={{ y: textY, opacity }} className="container-auvra relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-10"
          data-testid="hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-auvra-blue animate-pulse" />
          <span className="text-xs tracking-[0.2em] uppercase text-white/80">Institutional RWA Infrastructure</span>
        </motion.div>

        <h1 className="text-5xl md:text-7xl lg:text-[88px] font-semibold tracking-tighter leading-[1.02] max-w-5xl mx-auto">
          {LINES.map((line, li) => (
            <span key={li} className="line-mask">
              <motion.span
                variants={lineReveal}
                custom={li}
                initial="hidden"
                animate="show"
                className="inline-block"
              >
                {line.map((word, wi) =>
                  typeof word === "object" ? (
                    <span key={wi} className="text-auvra-blue">{word.text} </span>
                  ) : (
                    <span key={wi}>{word} </span>
                  )
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          variants={fadeUp}
          custom={6}
          initial="hidden"
          animate="show"
          className="text-base md:text-lg text-secondary-auvra max-w-2xl mx-auto mt-8 leading-relaxed"
        >
          Transforming high-value real-world assets into secure digital investments through
          compliant blockchain infrastructure. Institutional-grade. Globally accessible.
        </motion.p>

        <motion.div
          variants={fadeUp}
          custom={7}
          initial="hidden"
          animate="show"
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12"
        >
          <button
            onClick={onExplore}
            data-testid="hero-explore-btn"
            className="group flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-7 py-3.5 rounded-full transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.5)]"
          >
            Explore Platform
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={onInvest}
            data-testid="hero-invest-btn"
            className="border border-white/15 hover:border-white/40 text-white font-medium px-7 py-3.5 rounded-full transition-all hover:bg-white/5"
          >
            Start Investing
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
